import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Navigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
	const { user, loading: authLoading } = useAuth();

	// Debug logging to identify current user
	if (user && import.meta.env.DEV) {
		console.log('[AdminRoute] Current user ID:', user.id);
		console.log('[AdminRoute] Current user email:', user.email);
	}

	const {
		data: hasAdminRole,
		isLoading: roleLoading,
		error: roleError
	} = useQuery({
		queryKey: ['user-role', user?.id],
		enabled: !!user && !authLoading,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
		queryFn: async () => {
			const { data, error } = await supabase.rpc('has_role', {
				_user_id: user!.id,
				_role: 'admin'
			});

			if (error) {
				console.error('[AdminRoute] Error checking admin role:', error);
				throw error;
			}

			console.log('[AdminRoute] Has admin role:', data);
			return Boolean(data);
		}
	});

	// Show stable loading state until BOTH auth and role checks complete
	const isLoading = authLoading || (!!user && roleLoading);

	if (isLoading) {
		return (
			<div className="min-h-screen-safe flex items-center justify-center">
				<LoadingSpinner variant="panel" title="Verifying Access" />
			</div>
		);
	}

	// Handle error state
	if (roleError && user) {
		return (
			<div className="min-h-screen-safe flex items-center justify-center p-4">
				<div className="text-center space-y-4">
					<p className="text-destructive">Failed to verify admin access</p>
					<button
						onClick={() => window.location.reload()}
						className="text-sm text-muted-foreground hover:text-foreground"
					>
						Try again
					</button>
				</div>
			</div>
		);
	}

	// Only redirect after loading is complete and we have definitive results.
	// If unauthed, signal SignInHome to auto-open the auth tray.
	if (!user) {
		return <Navigate to="/?auth=1" replace />;
	}
	if (hasAdminRole === false) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
};
